import { useState } from 'react';
import { useHasPermission } from '@/auth/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { useT } from '@/i18n/use-t';
import {
  useTaxCategoryMappings,
  useSetTaxCategoryMapping,
} from '@/hooks/use-tax-category-mappings';
import { taxCategoryLabel, type TaxCategoryView } from '@/hooks/use-tax-rates';
import { extractApiError } from '@/lib/api-error';

export function TaxCategoryMappings({
  countryPackId,
  categories,
}: {
  countryPackId: string;
  categories: TaxCategoryView[];
}) {
  const t = useT();
  const canRead = useHasPermission('catalog.read');
  const canEdit = useHasPermission('catalog.admin');
  const mappings = useTaxCategoryMappings(canRead);
  const save = useSetTaxCategoryMapping();
  const [sourceSystem, setSource] = useState('');
  const [sourceTaxCategory, setRaw] = useState('');
  const [taxCategory, setCategory] = useState('');
  const [replace, setReplace] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!canRead) return null;
  return (
    <section className="space-y-3 rounded-md border p-4">
      <h2 className="text-lg font-semibold">{t('taxMappings.title', 'Source tax categories')}</h2>
      <p className="text-sm text-muted-foreground">
        {t(
          'taxMappings.help',
          'Map each exact source code to a canonical category. A conversion is keyed by source system and source code for the WHOLE tenant, not per country pack, so every conversion is listed here whichever pack it targets. Source codes are case sensitive; Latin and Cyrillic letters are different. Confirm their meaning with the source owner. Changes affect new ingestion; existing items require a separate replay.',
        )}
      </p>
      {mappings.isLoading && <p>{t('common.loading', 'Loading…')}</p>}
      {mappings.isError && (
        <p role="alert" className="text-destructive">
          {t('taxMappings.loadError', 'Could not load source mappings.')}
        </p>
      )}
      <Table>
        <THead>
          <TR>
            <TH>{t('taxMappings.source', 'Source')}</TH>
            <TH>{t('taxMappings.raw', 'Source code')}</TH>
            <TH>{t('taxMappings.pack', 'Country pack')}</TH>
            <TH>{t('taxMappings.target', 'Category')}</TH>
            <TH />
          </TR>
        </THead>
        <TBody>
          {mappings.data?.map((row) => (
            <TR key={JSON.stringify([row.sourceSystem, row.sourceTaxCategory])}>
              <TD className="font-mono">{row.sourceSystem}</TD>
              <TD className="whitespace-pre font-mono">{row.sourceTaxCategory}</TD>
              <TD className="font-mono">{row.countryPackId}</TD>
              <TD>{row.taxCategory}</TD>
              <TD>
                {canEdit && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSource(row.sourceSystem);
                      setRaw(row.sourceTaxCategory);
                      setCategory(row.countryPackId === countryPackId ? row.taxCategory : '');
                      setReplace(row.countryPackId !== countryPackId);
                      setError(null);
                    }}
                  >
                    {t('common.edit', 'Edit')}
                  </Button>
                )}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      {canEdit && (
        <form
          className="grid gap-3 md:grid-cols-2 xl:grid-cols-4"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            save.mutate(
              {
                sourceSystem,
                sourceTaxCategory,
                countryPackId,
                taxCategory,
                ...(replace ? { replace: true } : {}),
              },
              {
                onError: (err) => setError(extractApiError(err)),
                onSuccess: () => {
                  setRaw('');
                  setReplace(false);
                },
              },
            );
          }}
        >
          <label className="space-y-1 text-sm">
            {t('taxMappings.source', 'Source')}
            <Input
              required
              pattern="[a-z0-9][a-z0-9._-]{0,63}"
              maxLength={64}
              value={sourceSystem}
              disabled={save.isPending}
              placeholder="gp-compat / csv"
              onChange={(e) => setSource(e.target.value)}
            />
          </label>
          <label className="space-y-1 text-sm">
            {t('taxMappings.raw', 'Source code')}
            <Input
              required
              maxLength={128}
              value={sourceTaxCategory}
              disabled={save.isPending}
              onChange={(e) => setRaw(e.target.value)}
            />
          </label>
          <label className="space-y-1 text-sm">
            {t('taxMappings.target', 'Category')}
            <Select
              required
              value={taxCategory}
              disabled={save.isPending}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">{t('taxMappings.select', 'Select category')}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {taxCategoryLabel(t, category)} ({category.id})
                </option>
              ))}
            </Select>
          </label>
          <label className="flex items-center gap-2 self-end text-sm">
            <input
              type="checkbox"
              checked={replace}
              disabled={save.isPending}
              onChange={(e) => setReplace(e.target.checked)}
            />
            {t('taxMappings.replace', 'Re-point an existing conversion')}
          </label>
          <Button
            className="self-end"
            type="submit"
            disabled={save.isPending || categories.length === 0}
          >
            {t('common.save', 'Save')}
          </Button>
          {error !== null && (
            <p role="alert" className="text-destructive md:col-span-2 xl:col-span-4">
              {error}
            </p>
          )}
        </form>
      )}
    </section>
  );
}
